import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, type SxProps} from '@mui/material';
import Grid from '@mui/material/Grid';
import {skipToken} from '@reduxjs/toolkit/query/react';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router';
import {Concept, type NoProps} from 'types';
import {useLocalization} from 'localization';
import {useSetAdvancedSearchItems} from 'services/advancedSearchItems';
import {
  useGetUpdateLogQuery,
  useLazyGetUpdateLogEansQuery,
} from 'services/updateLog';
import {IconButton, Scene, Text} from 'components';
import type {DateRangeValue} from 'components/fields/DateRangeField';
import {DateRangeField} from 'components/fields/DateRangeField';
import {Table} from 'schema';
import {useColumnConfigState} from 'schema/table/hooks/useColumnConfigState';
import {updateLogTableConfig} from 'schemas';
import type {UpdateLogRow} from 'schemas/schemas';
import {updateLogSchema} from 'schemas/schemas';
import {useDateRangeShortcuts} from './hooks';

const API_DATE_FORMAT = 'YYYY-MM-DD';
const sxContainer: SxProps = {
  maxWidth: 1200,
};

export const UpdateLogScene: React.FC<NoProps> = () => {
  const {t, tLoose} = useLocalization();
  const navigate = useNavigate();
  const shortcuts = useDateRangeShortcuts();
  const title = t('appbar.updateLog');

  const [dateRange, setDateRange] = useState<DateRangeValue>([
    dayjs().subtract(30, 'day'),
    dayjs(),
  ]);

  const {paginationModel, resetPaginationModel} = useColumnConfigState(
    updateLogSchema.name,
  );

  const from = dateRange[0]?.format(API_DATE_FORMAT);
  const to = dateRange[1]?.format(API_DATE_FORMAT);

  const {data, isFetching} = useGetUpdateLogQuery(
    from && to
      ? {
          from,
          to,
          page: paginationModel.page,
          size: paginationModel.pageSize,
        }
      : skipToken,
  );
  const [getUpdateLogsEans] = useLazyGetUpdateLogEansQuery();
  const getAllEans = useCallback(() => {
    if (!from || !to || !data?.elementCount) {
      return Promise.resolve([]);
    }
    return getUpdateLogsEans({from, to, size: data.elementCount}).unwrap();
  }, [data, from, getUpdateLogsEans, to]);

  // Reset to the first page whenever the selected period changes.
  useEffect(() => {
    resetPaginationModel();
  }, [from, to, resetPaginationModel]);

  const rows = useMemo<UpdateLogRow[]>(
    () =>
      (data?.items ?? []).map(item => ({
        id: `${item.ean}-${item.created}`,
        created: item.created,
        ean: item.ean,
        updatedBy: item.updatedBy,
        status: item.statusChanged
          ? tLoose(`entity.manifestation.status.full.${item.statusChanged}`)
          : undefined,
      })),
    [data, tLoose],
  );

  const setSearchItems = useSetAdvancedSearchItems();
  const handleOpenInSearch = useCallback(
    (eans: string[]) => {
      setSearchItems({type: Concept.manifestation, itemIds: eans});
      return navigate(`/search`);
    },
    [navigate, setSearchItems],
  );

  return (
    <Scene browserTitle={title}>
      <Scene.Header title={title} />
      <Scene.Content>
        <Grid container sx={sxContainer} spacing={2}>
          <Grid size={4}>
            <DateRangeField
              label={t('page.updateLog.dateRange.label')}
              value={dateRange}
              onChange={value => value && setDateRange(value)}
              shortcuts={shortcuts}
            />
          </Grid>
          <Grid size={12}>
            <Table<UpdateLogRow>
              schema={updateLogSchema}
              configuration={updateLogTableConfig}
              data={rows}
              rowCount={data?.elementCount ?? 0}
              onRowClick={row => {
                navigate(`/metadata/ean/${row.ean}`);
              }}
              header={
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    width: '100%',
                  }}>
                  <Box>
                    <IconButton
                      icon={'Search'}
                      size={'small'}
                      onClick={() => {
                        getAllEans().then(eans => {
                          handleOpenInSearch(eans);
                        });
                      }}
                      postText={<Text variant={'body1'}>{'Åpne i søk'}</Text>}
                    />
                  </Box>
                </Box>
              }
              loading={isFetching}
            />
          </Grid>
        </Grid>
      </Scene.Content>
    </Scene>
  );
};
